import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

// ---------- Тема ----------

export type Theme = 'light' | 'dark' | 'system';

function readTheme(): Theme {
  try {
    const t = localStorage.getItem('edms-theme');
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(readTheme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') delete root.dataset.theme;
    else root.dataset.theme = theme;
    try {
      if (theme === 'system') localStorage.removeItem('edms-theme');
      else localStorage.setItem('edms-theme', theme);
    } catch {
      /* приватный режим — тема просто не запомнится */
    }
  }, [theme]);
  return [theme, setTheme];
}

// ---------- Всплывающие сообщения ----------

interface Toast {
  id: number;
  text: string;
  error: boolean;
}
type Notify = (text: string, opts?: { error?: boolean }) => void;
const ToastContext = createContext<Notify>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const notify = useCallback<Notify>((text, opts) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, error: Boolean(opts?.error) }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);
  return (
    <ToastContext value={notify}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.error ? ' error' : ''}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export const useToast = () => useContext(ToastContext);

// ---------- Мелкие компоненты ----------

function hue(seed: string): number {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  return (
    <span className="avatar" aria-hidden style={{ ['--h' as string]: hue(name), ['--size' as string]: `${size}px` }}>
      {initials(name)}
    </span>
  );
}

export function Person({ name, sub }: { name: string; sub?: string | null }) {
  return (
    <span className="person">
      <Avatar name={name} />
      <span style={{ minWidth: 0 }}>
        <b>{name}</b>
        {sub && <small>{sub}</small>}
      </span>
    </span>
  );
}

export function Empty({ emoji, title, children }: { emoji: string; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <span className="emoji" aria-hidden>
        {emoji}
      </span>
      <b>{title}</b>
      {children}
    </div>
  );
}

const dateFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
export function formatDate(iso: string | null | undefined): string {
  return iso ? dateFmt.format(new Date(iso)) : '—';
}

export function relative(iso: string | null | undefined): string {
  if (!iso) return 'ещё не входил';
  const diff = (Date.now() - new Date(iso).getTime()) / 60_000;
  if (diff < 2) return 'только что';
  if (diff < 60) return `${Math.round(diff)} мин назад`;
  if (diff < 24 * 60) return `${Math.round(diff / 60)} ч назад`;
  return formatDate(iso);
}

export function Pager({ total, limit, offset, onChange }: { total: number; limit: number; offset: number; onChange: (offset: number) => void }) {
  if (total <= limit) return null;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.ceil(total / limit);
  return (
    <div className="pager">
      <span className="faint">
        {offset + 1}–{Math.min(offset + limit, total)} из {total}
      </span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-ghost" disabled={page <= 1} onClick={() => onChange(offset - limit)}>
          ← Назад
        </button>
        <button className="btn btn-ghost" disabled={page >= pages} onClick={() => onChange(offset + limit)}>
          Вперёд →
        </button>
      </span>
    </div>
  );
}

/** Значение с задержкой — для поиска по мере ввода. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
